// JavaScript files are compiled and minified during the build process to the assets/built folder. See available scripts in the package.json file.

// Import CSS
import "../css/index.css";

// Import React components and utilities
import ComponentRenderer from "./components/ComponentRenderer.js";
import MotionPageLoader from "./components/MotionPageLoader.js";
import LazyAnimations from "./components/LazyAnimations.js";
import BlogPostAnimations from "./components/BlogPostAnimations.js";
import FeaturedImageCaption from "./components/FeaturedImageCaption.js";
import "./components/AnimatedBackground.js";

// Import legacy JS functions
import menuOpen from "./menuOpen";
import infiniteScroll from "./infiniteScroll";

// Version information
const THEME_VERSION = '2.1.5';
const BUILD_TIME = new Date().toISOString();

// Theme initialization
class PotredTheme {
  constructor() {
    this.version = THEME_VERSION;
    this.buildTime = BUILD_TIME;
    this.components = new ComponentRenderer();
    this.motionLoader = null;
    this.lazyAnimations = null;
    this.blogPostAnimations = null;
    this.featuredImageCaption = null;

    console.log(`🎨 Potred Ghost Theme v${this.version} initialized at ${this.buildTime}`);
  }

  init() {
    // Handle bfcache (back-forward cache) restoration
    // When user navigates back, skip loading screen entirely
    this.setupBfcacheHandler();

    // Initialize motion-based page loading progress FIRST (before other components)
    this.initMotionPageLoader();

    // Initialize React components EXCEPT animated background (render behind loading screen)
    this.components.initWithoutBackground();

    // Initialize legacy functions
    menuOpen();
    infiniteScroll();

    // Initialize other theme features
    this.initOtherThemeFeatures();

    // Add version to global
    window.POTRED_THEME_VERSION = this.version;
  }

  /**
   * Detect if this is a back/forward navigation (from bfcache or history)
   * Uses PerformanceNavigationTiming API with fallback to deprecated navigation.type
   */
  isBackForwardNavigation() {
    // Try modern PerformanceNavigationTiming API first
    if (performance.getEntriesByType) {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        const navType = navEntries[0].type;
        if (navType === 'back_forward') {
          console.log('📦 Detected back/forward navigation via PerformanceNavigationTiming');
          return true;
        }
      }
    }

    // Fallback to deprecated but widely supported API
    if (performance.navigation && performance.navigation.type === 2) {
      console.log('📦 Detected back/forward navigation via performance.navigation');
      return true;
    }

    return false;
  }

  setupBfcacheHandler() {
    // Handle bfcache restoration (back/forward navigation)
    // This is a fallback for cases where isBackForwardNavigation() didn't catch it
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        // Page was restored from bfcache - skip loading screen
        console.log('📦 Page restored from bfcache, skipping loading screen');

        // Immediately hide any loading screen
        if (this.motionLoader) {
          this.motionLoader.setAutoHideSuppressed(false);
          this.motionLoader.hide();
        }

        // Ensure animated background is visible
        this.components.renderAnimatedBackground();

        // Ensure main content is visible
        document.querySelector('.gh-main')?.classList.add('loaded');
      }
    });

    // Also mark when we're about to navigate away (for bfcache eligibility)
    window.addEventListener('pagehide', (event) => {
      if (event.persisted) {
        console.log('📦 Page entering bfcache');
      }
    });
  }

  initOtherThemeFeatures() {
    // Initialize lazy animations and stagger effects
    this.initLazyAnimations();

    // Initialize featured image captions
    this.initFeaturedImageCaptions();

    // Smooth scrolling for anchor links
    this.initSmoothScrolling();

    // Page transitions
    this.initPageTransitions();

    // Lazy loading for images
    this.initLazyLoading();

    // Performance monitoring
    this.initPerformanceMonitoring();
  }

  initMotionPageLoader() {
    // Detect network speed for adaptive duration
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const effectiveType = connection?.effectiveType || '4g';

    // Match animation duration to network-aware maximum loading time
    let duration = 3000; // Default for 4G/fast networks
    if (effectiveType === 'slow-2g' || effectiveType === '2g') {
      duration = 8000; // 8 seconds for very slow networks
    } else if (effectiveType === '3g') {
      duration = 6000; // 6 seconds for 3G networks
    } else if (effectiveType === '4g') {
      duration = 4000; // 4 seconds for 4G networks (slower than wifi)
    }

    this.motionLoader = new MotionPageLoader({
      duration: duration, // Network-aware duration
      customMessage: 'Loading...',
      showOnNavigation: true
    });

    // Show loading on initial page load (like Next.js behavior)
    this.showInitialLoading();
  }
  
  showInitialLoading() {
    // Skip loading screen entirely for back/forward navigation
    // The page is already fully loaded from cache, no need to show loading animation
    if (this.isBackForwardNavigation()) {
      console.log('📦 Skipping loading screen for back/forward navigation');
      this.components.renderAnimatedBackground();
      document.querySelector('.gh-main')?.classList.add('loaded');
      this.motionLoader.setAutoHideSuppressed(false);
      return;
    }

    // Only show progress loader on homepage (where carousel with heavy images exists)
    const isHomepage = document.getElementById('potred-carousel-mount');
    if (!isHomepage) {
      this.components.renderAnimatedBackground();
      // Add 'loaded' class to show main content
      document.querySelector('.gh-main')?.classList.add('loaded');
      this.motionLoader.setAutoHideSuppressed(false);
      return; // Skip loading screen for non-homepage pages
    }

    this.motionLoader.setAutoHideSuppressed(true);

    // Network-aware timeout detection (keeping structure for future tuning)
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const effectiveType = connection?.effectiveType || '4g';

    // Network-aware thresholds (all honour 3000ms max, ≥300ms per stage)
    const stageMinTimes = {
      '4g': { perStage: 300, total: 3000 },
      'wifi': { perStage: 300, total: 3000 },
      '3g': { perStage: 450, total: 3000 },
      'slow-2g': { perStage: 600, total: 3000 }
    };

    const thresholds = stageMinTimes[effectiveType] || stageMinTimes['4g'];
    const maxLoadingTime = thresholds.total; // Always 3000ms
    const stageMinDuration = thresholds.perStage || 300;
    console.log(`📊 Network: ${effectiveType}, Max loading time: ${maxLoadingTime}ms`);

    // Loading stages with clear milestones
    const STAGES = {
      COMPONENT_READY: { progress: 20, message: 'Loading frameworks...' },
      JS_LOADED: { progress: 40, message: 'Loading fonts...' },
      FONTS_LOADED: { progress: 60, message: 'Loading images...' },
      IMAGE_LOADED: { progress: 100, message: 'Ready!' }
    };

    let currentProgress = 0;
    let completionTriggered = false;
    let forced = false;
    let forceTimeoutId = null;
    const startTime = Date.now();
    const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Stage progression with updateProgress method
    const updateProgress = (stageName) => {
      const stage = STAGES[stageName];
      currentProgress = stage.progress;
      this.motionLoader.updateProgress(currentProgress, stage.message);
      console.log(`📊 Progress: ${currentProgress}% - ${stage.message} (${Date.now() - startTime}ms elapsed)`);
    };

    // Completion logic (minimum duration = 0ms for fast networks)
    const completeLoading = () => {
      if (completionTriggered) return;
      completionTriggered = true;

      if (forceTimeoutId) {
        clearTimeout(forceTimeoutId);
        forceTimeoutId = null;
      }

      const elapsed = Date.now() - startTime;
      const minDuration = 0; // Zero minimum as requested
      const remainingTime = Math.max(0, minDuration - elapsed);

      console.log(`✅ Loading complete after ${elapsed}ms, hiding in ${remainingTime}ms`);

      const finalize = () => {
        this.motionLoader.setAutoHideSuppressed(false);
        this.motionLoader.hide();
        this.components.renderAnimatedBackground();
        document.querySelector('.gh-main')?.classList.add('loaded');
      };

      if (remainingTime === 0) {
        finalize();
      } else {
        setTimeout(finalize, remainingTime);
      }
    };

    // Parse carousel data BEFORE stage execution (data parsing is synchronous)
    const base64DataScript = document.getElementById('carousel-data-base64');
    const dataScript = document.getElementById('carousel-data');
    let posts = [];

    if (base64DataScript) {
      try {
        // Parse pipe-delimited data format (same as ComponentRenderer)
        const rawData = base64DataScript.textContent?.trim() || '';
        if (rawData) {
          const postEntries = rawData.split('||');
          posts = postEntries.map(entry => {
            const [id, title, excerpt, url, feature_image, published_at, reading_time, tag_name, tag_slug, tag_color] = entry.split('|');
            return {
              id: id || '',
              title: title || '',
              excerpt: excerpt || '',
              url: url || '',
              feature_image: feature_image === 'null' ? null : feature_image,
              published_at: published_at || '',
              reading_time: parseInt(reading_time) || 5,
              primary_tag: tag_name === 'null' ? null : { name: tag_name, slug: tag_slug, accent_color: tag_color || '#ea580c' }
            };
          }).filter(post => post.id); // Filter out empty entries

          console.log('📦 Loading screen: Parsed', posts.length, 'posts from pipe-delimited data');
        }
      } catch (parseError) {
        console.warn('❌ Loading screen: Failed to parse pipe-delimited data:', parseError);
        posts = [];
      }
    } else if (dataScript) {
      try {
        // Fallback: JSON format
        let jsonContent = dataScript.textContent || '[]';
        jsonContent = jsonContent
          .replace(/\r?\n/g, ' ')
          .replace(/\s+/g, ' ')
          .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, ' ')
          .trim();
        posts = JSON.parse(jsonContent);
        console.log('📦 Loading screen: Parsed', posts.length, 'posts from JSON data');
      } catch (parseError) {
        console.warn('❌ Loading screen: Failed to parse JSON data:', parseError);
        posts = [];
      }
    }

    // Stage helpers to coordinate resource-aware loading stages
    const createForceSignal = () => {
      let resolved = false;
      let resolver = () => {};
      const promise = new Promise(resolve => {
        resolver = (value) => {
          if (resolved) return;
          resolved = true;
          resolve(value);
        };
      });
      return { promise, resolve: resolver };
    };

    const createFontLoadPromise = () => {
      if (!document.fonts) {
        return wait(300);
      }

      if (document.fonts.check('1em "Iansui"')) {
        return Promise.resolve({ success: true, reason: 'cached' });
      }

      // Proactively trigger font load to avoid idle state
      document.fonts.load('1em "Iansui"').catch(() => {});

      return new Promise(resolve => {
        let settled = false;

        const finish = (status) => {
          if (settled) return;
          settled = true;
          document.fonts.removeEventListener('loadingdone', onComplete);
          document.fonts.removeEventListener('loadingerror', onError);
          clearTimeout(timeoutId);
          resolve(status);
        };

        const onComplete = () => finish({ success: true, reason: 'event' });
        const onError = () => finish({ success: false, reason: 'error' });
        const timeoutId = setTimeout(() => finish({ success: false, reason: 'timeout' }), 4000);

        document.fonts.addEventListener('loadingdone', onComplete, { once: true });
        document.fonts.addEventListener('loadingerror', onError, { once: true });

        document.fonts.ready?.then(onComplete).catch(onError);
      });
    };

    const createImagePreloadPromise = (url) => {
      if (!url) {
        return Promise.resolve({ success: false, reason: 'missing', url: null });
      }

      return new Promise(resolve => {
        const preloadImage = new Image();
        let settled = false;

        const finalize = (status) => {
          if (settled) return;
          settled = true;
          preloadImage.onload = null;
          preloadImage.onerror = null;
          resolve({ ...status, url });
        };

        const handleLoad = () => {
          if (preloadImage.decode) {
            preloadImage.decode().then(() => finalize({ success: true, reason: 'decoded' })).catch(() => finalize({ success: true, reason: 'loaded' }));
          } else {
            finalize({ success: true, reason: 'loaded' });
          }
        };

        const handleError = () => finalize({ success: false, reason: 'error' });

        preloadImage.onload = handleLoad;
        preloadImage.onerror = handleError;

        try {
          preloadImage.src = url;
          if (preloadImage.complete && preloadImage.naturalWidth > 0) {
            handleLoad();
          }
        } catch (error) {
          console.warn('❌ Failed to initiate preload for first carousel image:', url, error);
          finalize({ success: false, reason: 'exception' });
        }
      });
    };

    const forcedSignal = createForceSignal();
    const fontsReadyPromise = createFontLoadPromise();
    const firstImageUrl = posts[0]?.feature_image || null;
    const firstImagePromise = createImagePreloadPromise(firstImageUrl);

    // Stage 1: Component mounted (0% → 20%) - Maintain minimum display time
    this.motionLoader.show(STAGES.COMPONENT_READY.message, STAGES.COMPONENT_READY.progress);

    const runLoaderSequence = async () => {
      // Stage 1: Component ready (0 → 20%)
      updateProgress('COMPONENT_READY');
      await Promise.race([wait(stageMinDuration), forcedSignal.promise]);
      if (completionTriggered) return;

      // Stage 2: JS frameworks ready (20 → 40%)
      updateProgress('JS_LOADED');
      await Promise.race([wait(stageMinDuration), forcedSignal.promise]);
      if (completionTriggered) return;

      // Stage 3: Font readiness with fallback timeout
      const fontOutcome = await Promise.race([fontsReadyPromise, forcedSignal.promise]);
      if (completionTriggered) return;

      if (fontOutcome?.forced) {
        console.warn('⚠️ Font loading forced to advance after timeout');
      } else if (fontOutcome?.success) {
        console.log('✅ Iansui font ready for rendering');
      } else {
        console.warn('⚠️ Iansui font load timed out or failed, continuing with fallback');
      }

      updateProgress('FONTS_LOADED');
      await Promise.race([wait(stageMinDuration), forcedSignal.promise]);
      if (completionTriggered) return;

      // Stage 4: First carousel image preload
      const imageOutcome = await Promise.race([firstImagePromise, forcedSignal.promise]);
      if (completionTriggered) return;

      if (imageOutcome?.forced) {
        console.warn('⚠️ First carousel image preload forced to complete after timeout');
      } else if (imageOutcome?.success && imageOutcome.url) {
        console.log('✅ First carousel image fully loaded:', imageOutcome.url);
      } else if (imageOutcome?.url) {
        console.warn('❌ Failed to load first carousel image:', imageOutcome.url);
      } else {
        console.log('ℹ️ No carousel image to preload');
      }

      updateProgress('IMAGE_LOADED');
      completeLoading();
    };

    // CRITICAL: Force completion at maximum threshold regardless of image state
    forceTimeoutId = setTimeout(() => {
      if (completionTriggered) {
        return;
      }
      forced = true;
      console.warn(`⏱️ ${maxLoadingTime}ms threshold reached, forcing completion`);
      forcedSignal.resolve({ forced: true });
    }, maxLoadingTime);

    runLoaderSequence().catch(error => {
      console.error('❌ Loader sequence error:', error);
      if (!completionTriggered) {
        updateProgress('IMAGE_LOADED');
        completeLoading();
      }
    });

  }

  updateLoadingMessage() {
    // Update the loading message based on current state
    const getLoadingMessage = () => {
      const firstImageLoaded = this.firstImageLoaded || false;
      const fontsLoaded = this.fontsLoaded || false;
      
      if (firstImageLoaded && fontsLoaded) {
        return "Ready!";
      } else if (firstImageLoaded) {
        return "Loading fonts...";
      } else if (fontsLoaded) {
        return "Loading images...";
      } else {
        return "Loading resources...";
      }
    };
    
    // Note: This is a simplified version - the full implementation would
    // require rebuilding the React component with new message
    // For now, the message is set once at the beginning
  }

  initLazyAnimations() {
    this.lazyAnimations = new LazyAnimations({
      threshold: 0.1,
      rootMargin: '50px',
      staggerDelay: 100,
      animationDuration: 600
    });

    // Initialize blog post animations for enhanced content experience
    this.blogPostAnimations = new BlogPostAnimations();
  }

  initFeaturedImageCaptions() {
    // Initialize featured image captions for post pages
    this.featuredImageCaption = new FeaturedImageCaption();
    this.featuredImageCaption.init();
  }

  initPageTransitions() {
    // Add page transition classes to main content
    const mainContent = document.querySelector('.gh-main, main, #main');
    if (mainContent) {
      mainContent.classList.add('page-transition-enter');
      
      // Animate in after a short delay
      setTimeout(() => {
        mainContent.classList.add('page-transition-enter-active');
        mainContent.classList.remove('page-transition-enter');
      }, 100);
    }
  }

  initSmoothScrolling() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  initLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              img.classList.add('animate-fade-in');
              observer.unobserve(img);
            }
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  initPerformanceMonitoring() {
    // Monitor Core Web Vitals
    if ('web-vital' in window) {
      web-vital.getCLS(console.log);
      web-vital.getFID(console.log);
      web-vital.getLCP(console.log);
    }
  }

  // Method to update version and trigger reload if needed
  checkForUpdates() {
    const currentVersion = localStorage.getItem('potred-theme-version');
    if (currentVersion && currentVersion !== this.version) {
      console.log(`🔄 Theme updated from v${currentVersion} to v${this.version}`);
      // Clear any cached resources if needed
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(registration => {
          return registration.update();
        });
      }
    }
    localStorage.setItem('potred-theme-version', this.version);
  }
}

// Initialize theme
const potredTheme = new PotredTheme();
potredTheme.checkForUpdates();
potredTheme.init();

// Export for global access
window.PotredTheme = potredTheme;